/**
 * @fileoverview Connect recorded outreach drafts and delivery policy to shared editor/presentation state. `OutreachWorkspace.js` owns explicit actions; `intakeApi.js` owns backend requests. Helpers preserve the distinction between suggested content, saved drafts, delivery configuration, and actual sends. Recipient/delivery notices describe backend policy without authorizing a message.
 */

import { formatDate, getActivityDetail, getTimestamp, INFORMATION_REQUEST_STAGE_KEYS, titleize } from './intakeFormatters';
import { EDITABLE_OUTREACH_STATUSES } from './intakeStatuses';
import { formatStageValue, formatStructuredListItem } from './IntakeAgentOutput';

// Outreach draft helpers: form building, delivery-policy notices and toasts,
// draft labels, and the per-stage action items shown in the stage timeline.



export function getDefaultOutreachForm(lead) {
  return {
    message_type: 'email',
    recipient_name: lead.prospect_name || '',
    recipient_email: lead.prospect_email || '',
    recipient_phone: lead.prospect_phone || '',
    subject: '',
    body: '',
    requested_info: ''
  };
}



function parseRequestedInfo(value) {
  if (!value) return '';
  if (Array.isArray(value)) return cleanRequestedInfoForEditor(value.map(formatStructuredListItem).filter(Boolean).join('\n'));
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return cleanRequestedInfoForEditor(parsed.map(formatStructuredListItem).filter(Boolean).join('\n'));
    if (parsed && typeof parsed === 'object') return cleanRequestedInfoForEditor(formatStageValue(parsed));
  } catch (error) {
    return cleanRequestedInfoForEditor(value);
  }
  return cleanRequestedInfoForEditor(String(value));
}

// Workflow automation stores provenance beside the requested items.  That is
// useful to an auditor, but it is not part of a staff member's client-facing
// request and should never be copied back into an outreach draft.


export function cleanRequestedInfoForEditor(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const metadataStart = text.search(/\s*\/\s*(?:Source Agent Run Id|Source Stage Run Id|Upload Mode|Upload Url|Pipedrive Person Id|Requires Pipedrive Person Id|Dev Upload Simulator Enabled|Manual Requested Info|Followup For Outreach Message Id|Followup Round|Followup Case Tier|Followup Generated By|Source):/i);
  return (metadataStart >= 0 ? text.slice(0, metadataStart) : text)
    .replace(/\s*\/\s*/g, '\n')
    .split('\n')
    .filter((line) => !/^\s*(Why Needed|Priority):/i.test(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}



export function buildOutreachForm(lead, row) {
  if (!row) return getDefaultOutreachForm(lead);
  return {
    message_type: row.message_type || 'email',
    recipient_name: row.recipient_name || lead?.prospect_name || '',
    recipient_email: row.recipient_email || lead?.prospect_email || '',
    recipient_phone: row.recipient_phone || lead?.prospect_phone || '',
    subject: row.subject || '',
    body: row.body || '',
    requested_info: parseRequestedInfo(row.requested_info_json)
  };
}



export function buildOutreachPayload(form) {
  return {
    ...form,
    requested_info: form.requested_info.split('\n').map((item) => item.trim()).filter(Boolean)
  };
}



function normalizeEmailForCompare(value) {
  return String(value || '').trim().toLowerCase();
}



export function getOutreachDeliveryNotice(policy, recipientEmail) {
  if (!policy) return null;
  const mode = String(policy.mode || 'live').toLowerCase();
  const originalRecipient = recipientEmail || policy.original_recipient_email || '';
  const redirectEmail = policy.redirect_email || (mode === 'redirect' ? policy.delivered_recipient_email : '');
  if (mode === 'redirect') {
    if (!redirectEmail) {
      return {
        icon: 'pi pi-exclamation-triangle',
        tone: 'danger',
        title: 'Outreach Redirect Misconfigured',
        detail: 'Redirect mode is active, but no dev recipient is configured. Dev-account sends are blocked until INTAKE_OUTREACH_REDIRECT_EMAIL is set.',
        sendLabel: 'Send Email',
        ready: false
      };
    }
    const replacesProspect = originalRecipient && normalizeEmailForCompare(originalRecipient) !== normalizeEmailForCompare(redirectEmail);
    return {
      icon: 'pi pi-share-alt',
      tone: 'warning',
      title: 'Outreach Redirect Active',
      detail: `Dev-account sends go only to ${redirectEmail}${replacesProspect ? ` instead of ${originalRecipient}` : ''}. Use actual-person send only when the draft is ready for the prospect.`,
      sendLabel: 'Send to Dev Account',
      ready: true
    };
  }
  if (mode === 'dry_run') {
    return {
      icon: 'pi pi-eye',
      tone: 'info',
      title: 'Outreach Dry Run Active',
      detail: `No email will be sent to the prospect.${redirectEmail ? ` Dry-run records show ${redirectEmail} as the delivery preview.` : ''}`,
      sendLabel: 'Record Dry Run',
      ready: true
    };
  }
  return {
    icon: 'pi pi-check-circle',
    tone: 'success',
    title: 'Live Outreach Delivery',
    detail: originalRecipient ? `Email sends to ${originalRecipient}.` : 'Email sends to the recipient in the To field.',
    sendLabel: 'Send Email',
    ready: true
  };
}



export function getOutreachSendToast(policy, recipientEmail) {
  const notice = getOutreachDeliveryNotice(policy, recipientEmail);
  const mode = String(policy?.mode || 'live').toLowerCase();
  if (mode === 'redirect') {
    return {
      severity: 'success',
      summary: 'Sent to Dev Account',
      detail: notice?.detail || 'Outreach email sent to the configured dev email.'
    };
  }
  if (mode === 'dry_run' || policy?.dry_run) {
    return {
      severity: 'info',
      summary: 'Dry Run Recorded',
      detail: 'No email was sent to the prospect.'
    };
  }
  return { severity: 'success', summary: 'Sent to Actual Person', detail: 'Outreach email sent' };
}



function getOutreachDeliveryState(row, fallbackPolicy = null) {
  const delivery = row?.delivery && typeof row.delivery === 'object' ? row.delivery : {};
  const gmailMessageId = delivery.gmail_message_id || row?.gmail_message_id || '';
  const gmailThreadId = delivery.gmail_thread_id || row?.gmail_thread_id || '';
  const detectedDryRun = Boolean(delivery.dry_run || row?.dry_run || String(gmailMessageId).startsWith('dry-run-intake-outreach-'));
  let mode = String(delivery.mode || row?.delivery_mode || '').trim().toLowerCase();
  if (!mode && detectedDryRun) mode = 'dry_run';
  if (!mode && row?.status !== 'sent' && fallbackPolicy?.mode) mode = String(fallbackPolicy.mode).trim().toLowerCase();
  if (!mode) mode = 'live';

  const intendedRecipient = (
    delivery.original_recipient_email ||
    row?.original_recipient_email ||
    row?.recipient_email ||
    fallbackPolicy?.original_recipient_email ||
    ''
  );
  let deliveredRecipient = delivery.delivered_recipient_email || row?.delivered_recipient_email || '';
  if (!deliveredRecipient && mode === 'redirect') {
    deliveredRecipient = fallbackPolicy?.delivered_recipient_email || fallbackPolicy?.redirect_email || '';
  }
  if (!deliveredRecipient && mode === 'live') {
    deliveredRecipient = intendedRecipient || row?.recipient_email || '';
  }

  return {
    mode,
    dryRun: detectedDryRun || mode === 'dry_run',
    intendedRecipient,
    deliveredRecipient,
    gmailMessageId,
    gmailThreadId
  };
}



function getOutreachDeliveryModeLabel(row, fallbackPolicy = null) {
  const delivery = getOutreachDeliveryState(row, fallbackPolicy);
  if (delivery.dryRun) return 'Dry run';
  if (delivery.mode === 'redirect') return 'Redirect';
  if (delivery.mode === 'live') return 'Live';
  return titleize(delivery.mode || 'delivery');
}



function getOutreachActionLabel(row, fallbackPolicy = null) {
  if (row.status === 'sent') {
    const delivery = getOutreachDeliveryState(row, fallbackPolicy);
    if (delivery.dryRun) return 'Dry run recorded';
    if (delivery.mode === 'redirect') return 'Email sent to tester';
    return 'Email sent';
  }
  if (row.status === 'skipped') return 'Outreach skipped';
  if (row.status === 'sending') return 'Email sending';
  return `${titleize(row.message_type)} ${titleize(row.status)}`;
}



function getOutreachDeliverySummary(row, fallbackPolicy = null) {
  const actionLabel = getOutreachActionLabel(row, fallbackPolicy);
  if (row.status !== 'sent') {
    return getActivityDetail([
      actionLabel,
      row.recipient_email || row.recipient_phone || row.recipient_name,
      row.send_error
    ]);
  }

  const delivery = getOutreachDeliveryState(row, fallbackPolicy);
  if (delivery.dryRun) {
    return getActivityDetail([
      actionLabel,
      delivery.intendedRecipient ? `Would send to ${delivery.intendedRecipient}` : null,
      'No email was sent'
    ]);
  }
  if (delivery.mode === 'redirect') {
    return getActivityDetail([
      actionLabel,
      delivery.intendedRecipient ? `Intended for ${delivery.intendedRecipient}` : null,
      delivery.deliveredRecipient ? `Sent to ${delivery.deliveredRecipient}` : null
    ]);
  }
  return getActivityDetail([
    actionLabel,
    delivery.deliveredRecipient ? `Sent to ${delivery.deliveredRecipient}` : null
  ]);
}



function getOutreachStatusLabel(row, fallbackPolicy = null) {
  if (row.status === 'sent') return getOutreachDeliveryModeLabel(row, fallbackPolicy);
  return titleize(row.status);
}



function getOutreachDetailFields(row, fallbackPolicy = null) {
  const delivery = getOutreachDeliveryState(row, fallbackPolicy);
  const fields = [
    ['Status', getOutreachActionLabel(row, fallbackPolicy)],
    ['Delivery mode', row.status === 'sent' ? getOutreachDeliveryModeLabel(row, fallbackPolicy) : null],
    ['Intended recipient', delivery.intendedRecipient || row.recipient_email || row.recipient_name],
  ];
  if (row.status === 'sent') {
    fields.push([
      'Actual delivery',
      delivery.dryRun ? 'No email was sent' : delivery.deliveredRecipient || 'Delivery target not recorded'
    ]);
  }
  fields.push(
    ['Sender', row.sender_email],
    ['Gmail message ID', delivery.gmailMessageId],
    ['Gmail thread ID', delivery.gmailThreadId],
    ['Recorded at', getDraftTimestamp(row) ? formatDate(getDraftTimestamp(row)) : null]
  );
  return fields.filter(([, value]) => value);
}



function getDraftTimestamp(row) {
  return row.sent_at || row.last_send_attempt_at || row.approved_at || row.updated_at || row.created_at;
}



export function getDraftSubtitle(row) {
  if (row.status === 'sent' && row.sent_at) return `Sent ${formatDate(row.sent_at)}`;
  if (row.status === 'send_failed' && row.send_error) return row.send_error;
  if (row.status === 'approved' && row.approved_at) return `Approved ${formatDate(row.approved_at)}`;
  const timestamp = getDraftTimestamp(row);
  return timestamp ? formatDate(timestamp) : titleize(row.message_type);
}



function pushStageAction(map, stageKey, item) {
  if (!stageKey) return;
  if (!map[stageKey]) map[stageKey] = [];
  map[stageKey].push(item);
}



export function buildStageActionItems(outreachRows, deliveryPolicy = null) {
  const actionsByStage = {};

  (outreachRows || []).filter((row) => !EDITABLE_OUTREACH_STATUSES.has(row.status)).forEach((row) => {
    const action = {
      id: `outreach-${row.id}`,
      time: getDraftTimestamp(row),
      fallbackOrder: row.id || 0,
      label: row.subject || getOutreachActionLabel(row, deliveryPolicy),
      detail: getOutreachDeliverySummary(row, deliveryPolicy),
      status: row.status,
      statusLabel: getOutreachStatusLabel(row, deliveryPolicy),
      outreach: {
        subject: row.subject || '',
        body: row.body || '',
        requestedInfo: parseRequestedInfo(row.requested_info_json),
        detailFields: getOutreachDetailFields(row, deliveryPolicy)
      }
	    };
    INFORMATION_REQUEST_STAGE_KEYS.forEach((stageKey) => pushStageAction(actionsByStage, stageKey, action));
  });

  Object.keys(actionsByStage).forEach((stageKey) => {
    actionsByStage[stageKey].sort((left, right) => {
      const timeDiff = getTimestamp(left.time) - getTimestamp(right.time);
      return timeDiff || left.fallbackOrder - right.fallbackOrder;
    });
  });

  return actionsByStage;
}
